import React from 'react';
import T from 'prop-types';
import cx from 'classnames';
import { isFinite } from 'lodash-es';

const normalizeThreshold = (threshold) => {
  const normalizedThreshold = [].concat(threshold).slice(0, 2);

  if (normalizedThreshold.length === 1) {
    normalizedThreshold.push(normalizedThreshold[0]);
  }

  return normalizedThreshold;
};

const ThresholdBadge = ({
  value,
  threshold = 0,
  aboveLabel = 'Above',
  belowLabel = 'Below',
  inlineLabel = 'Inline',
  displayMode = 'value',
  valueUnit,
  prefix,
  emptyValue = 'NA',
  oppositeColor = false,
  className,
}) => {
  const parsedValue = parseFloat(value);
  const isValueValid = isFinite(parsedValue);

  // Empty State
  if (!isValueValid) {
    return (
      <div
        className={cx('threshold-badge empty-state', className)}
        data-testid="threshold-badge-invalid"
      >
        <span className="empty-state__label">{emptyValue}</span>
      </div>
    );
  }

  // Threshold Label
  let thresholdLabel = inlineLabel;
  const [aboveThreshold, belowThreshold] = normalizeThreshold(threshold);

  if (parsedValue > aboveThreshold) {
    thresholdLabel = aboveLabel;
  } else if (parsedValue < belowThreshold) {
    thresholdLabel = belowLabel;
  }

  // Display Label
  let displayLabel = thresholdLabel;

  if (displayMode === 'value') {
    displayLabel = valueUnit ? (
      <>
        {value}
        {valueUnit}
      </>
    ) : (
      value
    );
  }

  return (
    <div
      className={cx(
        'threshold-badge',
        `mode-${displayMode}`,
        oppositeColor && 'mode-opposite-color',
        className,
        {
          above: oppositeColor ? thresholdLabel === belowLabel : thresholdLabel === aboveLabel,
          below: oppositeColor ? thresholdLabel === aboveLabel : thresholdLabel === belowLabel,
          inline: thresholdLabel === inlineLabel,
        }
      )}
      data-testid="threshold-badge"
    >
      {prefix}
      {displayLabel}
    </div>
  );
};

ThresholdBadge.propTypes = {
  value: T.oneOfType([T.number, T.string]),
  threshold: T.oneOfType([T.number, T.arrayOf(T.number)]),
  aboveLabel: T.string,
  belowLabel: T.string,
  inlineLabel: T.string,
  displayMode: T.oneOf(['value', 'label']),
  valueUnit: T.oneOfType([T.string, T.node]),
  emptyValue: T.node,
  oppositeColor: T.bool,
  prefix: T.string,
  className: T.string,
};

export default ThresholdBadge;
